import { useDispatch, useSelector } from 'react-redux';

import { ERuntimeSettings } from '../../common/types/runtimeSettings';
import { useApplicationContext } from '../../common/utils/applicationContext';
import { openAuthorizationModal } from '../redux';
import { selectDefaultRedirectUrl, selectIsAuthorized, selectShortFormGroup } from '../selectors/calculator';
import { selectFinancePlatformEnabled } from '../selectors/calculator/selectFinancePlatformEnabled';
import { ETypeAction } from '../types';
import { redirect } from '../utils';
import { IOfferForAnalytic } from '../utils/tracking/types';
import { useCianAuthorization } from './useCianAuthorization';
import { useSaveOffers } from './useSaveOffers';
import { useShortFormAuthAction } from './useShortFormAuthAction';

export interface IUseAuthActionOptions {
  trackingFunction?: () => void;
  redirectUrl?: string;
  offerForAnalytic?: IOfferForAnalytic | null;
  listNumber?: number | null;
  bankId?: number | null;
}

export interface IUseAuthActionReturn {
  isLoading: boolean;
  handleAuthAction: () => Promise<void> | void;
}

export const useAuthAction = (options: IUseAuthActionOptions = {}): IUseAuthActionReturn => {
  const { trackingFunction, redirectUrl, offerForAnalytic, listNumber, bankId } = options;
  const { config } = useApplicationContext();
  const dispatch = useDispatch();
  const isAuthorized = useSelector(selectIsAuthorized);
  const defaultRedirectUrl = useSelector(selectDefaultRedirectUrl);
  const financePlatformEnabled = useSelector(selectFinancePlatformEnabled);
  const handleCianAuthAction = useCianAuthorization();
  /* Группу считает бэкенд и отдаёт в meta v3-выдачи, откуда она попадает в стор:
     своего резолва на фронте нет, чтобы не разойтись с бэком */
  const shortFormGroup = useSelector(selectShortFormGroup);

  const isCianAuthorizationEnabled = config.getStrict<boolean>(ERuntimeSettings.isMortgageRedirectEnabled) ?? false;

  const { isLoading, saveOffers, shouldUseAuthActionV3 } = useSaveOffers({
    redirectUrl,
    defaultRedirectUrl,
  });

  const { isLoading: isShortFormLoading, handleShortFormAuthAction } = useShortFormAuthAction({
    trackingFunction,
    offerForAnalytic,
    listNumber,
    bankId,
  });

  const handleAuthActionV3 = async () => {
    trackingFunction?.();
    if (financePlatformEnabled || isAuthorized) {
      await saveOffers(ETypeAction.REDIRECT_ONLY);

      return;
    }

    await saveOffers(ETypeAction.AUTH_AND_REDIRECT);
  };

  const handleClickOld = () => {
    trackingFunction?.();

    if (financePlatformEnabled || isAuthorized) {
      redirect(defaultRedirectUrl);

      return;
    }

    dispatch(openAuthorizationModal({ redirectUrl: defaultRedirectUrl }));
  };

  const handleLegacyAuthAction = shouldUseAuthActionV3 ? handleAuthActionV3 : handleClickOld;

  const handleCianAuthActionWithTracking = () => {
    trackingFunction?.();

    return handleCianAuthAction();
  };

  const handleDefaultAuthAction = isCianAuthorizationEnabled
    ? handleCianAuthActionWithTracking
    : handleLegacyAuthAction;

  /* Без v3-выдачи короткую анкету наполнять нечем: выдачу не сохранить,
     человек уехал бы в анкету без расчёта. На откате к v2 группа B идёт по контролю */
  const handleAuthAction =
    shortFormGroup === 'B' && shouldUseAuthActionV3 ? handleShortFormAuthAction : handleDefaultAuthAction;

  return { isLoading: isLoading || isShortFormLoading, handleAuthAction };
};
